import Link from "ui/Link";
import Alert from "ui/Alert";
import DevLogin from "../DevLogin";

import GoogleLogo from "images/google-logo.svg";
import MsSignInLogo from "images/ms-logo-sign-in.svg";
import { SurfaceType } from "utils/analytics/analyticsConstants";
import { envWithDefault } from "utils/env";
import { AnalyticsProvider } from "utils/analytics/AnalyticsContext";
import Stack from "ui/Stack";
import Typography from "ui/Typography";
import Callout from "components/Callout";
import styled, { css } from "styled-components/macro";
import GuestLayout from "components/GuestLayout/GuestLayout";
import Button from "ui/Button";

const $Callout = styled(Callout)`
  right: -3.813rem;
  top: 2.2rem;
`;

const $PageHeader = styled(Typography).attrs({
  size: "h2",
  color: "ColorSpace500",
  fontWeight: "normal",
  className: "mb-4",
})`
  text-align: center;
`;

const $OldLoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10% 0.625rem 0;

  .mb-4 {
    padding: 0;
  }

  @media screen and (${({ theme }) => theme.BreakpointTabletDown}) {
    text-align: center;
  }
`;

const LoginContainer = styled($OldLoginContainer)`
  padding-top: ${({ theme }) => theme.SizeXxxl};
`;

const loginButtonWidth = "18.5rem";

// These buttons need a white background to comply with
// Google and Microsoft logo requirements.
const loginBtnBaseStyles = css`
  width: ${loginButtonWidth};
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.ColorWhite} !important;
  font-weight: bold;

  &:hover,
  &:focus,
  &.disabled {
    background-color: ${({ theme }) => theme.ColorWhite} !important;
  }
`;

const loginBtnGoogleStyles = css`
  img {
    max-width: 1.5rem;
    margin-right: 1rem;
  }
`;

const $GoogleLoginBtn = styled(Button)`
  ${loginBtnBaseStyles}
  ${loginBtnGoogleStyles}
`;

const loginBtnMicrosoftStyles = css`
  line-height: 0;
`;

const $MicrosoftLoginBtn = styled(Button)`
  ${loginBtnBaseStyles}
  ${loginBtnMicrosoftStyles}
`;
/*
  NOTE: width cannot be rem, it should not scale.
  TODO: Change MS icon once received from UX
*/
const $MicrosoftSignInLogoWrapper = styled.span`
  display: inline-block;

  width: 33px;
  overflow: hidden;
`;

const $MicrosoftSignInLogo = styled.img`
  width: 11.35rem;
`;

// Needs an important flag here to override the margin coming from parent <Stack/>
const $LoginBtnContainer = styled(Stack).attrs({
  $align: "center",
  $space: "SizeMd",
})`
  margin-bottom: 1.9rem !important;
`;

function getLoginButtons(viewOnly: boolean, queryParams: string) {
  return (
    <$GoogleLoginBtn
      variant="secondary"
      analyticsName="clicked_google_login"
      data-testid="google-login"
      href={"/auth/google_oauth2" + queryParams}
      requestInProgress={false}
      disabled={viewOnly}
    >
      <img src={GoogleLogo} alt="Google Logo" />
      Sign in with Google
    </$GoogleLoginBtn>
  );
}

type StudentLoginProps = {
  schoolName: string;
  connectCode: string;
  viewOnly?: boolean;
};
export default function StudentLogin({
  schoolName,
  connectCode,
  viewOnly = false,
}: StudentLoginProps) {
  const enableDevLoginRoutes =
    envWithDefault("ENABLE_DEV_LOGIN_ROUTES") === "true";
  const params = new URLSearchParams(window.location.search);
  const loginError = params.get("error");
  const queryParams = `?connect_code=${connectCode}`;
  if (loginError) {
    // clear the query param to dismiss the error
    window.history.pushState("clear", "", "?#");
  }

  function renderContent() {
    if (loginError) {
      return (
        <Typography
          size="h4"
          as="p"
          color="ColorWhite"
          textAlign="center"
          className="mb-4"
        >
          You’ll need an invite to sign in. If you’re a teacher, join our{" "}
          <Link
            href="https://www.along.org/"
            isOnDarkBackground={true}
            analyticsName="clicked_waitlist_link"
          >
            waitlist
          </Link>{" "}
          to get one. If you’re a student, ask your teacher for help.
        </Typography>
      );
    }

    return (
      <>
        <$PageHeader>
          <Stack>Connect with your teacher from</Stack>
          <Stack>{schoolName} on Along</Stack>
        </$PageHeader>
        <Typography size="bodySansSerif" as="p" className="mb-4">
          Sign in with your school email:
        </Typography>
        <$LoginBtnContainer>
          {!viewOnly ? (
            getLoginButtons(viewOnly, queryParams)
          ) : (
            <$Callout
              heading="Tester mode:"
              text="These buttons will be active for your students"
            >
              {getLoginButtons(viewOnly, queryParams)}
            </$Callout>
          )}
          <$MicrosoftLoginBtn
            variant="secondary"
            analyticsName="clicked_microsoft_login"
            href={"/auth/azure_oauth2" + queryParams}
            requestInProgress={false}
            disabled={viewOnly}
          >
            <$MicrosoftSignInLogoWrapper>
              <$MicrosoftSignInLogo src={MsSignInLogo} alt="Microsoft Logo" />
            </$MicrosoftSignInLogoWrapper>
            Sign in with Microsoft
          </$MicrosoftLoginBtn>
        </$LoginBtnContainer>
        {enableDevLoginRoutes && (
          <DevLogin isOnDarkBackground={false} connectCode={connectCode} />
        )}
      </>
    );
  }

  return (
    <AnalyticsProvider surface={SurfaceType.USER_LOGIN}>
      <GuestLayout userType="student" showSignout={false}>
        {loginError && <Alert variant="danger">{loginError}</Alert>}
        <LoginContainer>{renderContent()}</LoginContainer>
      </GuestLayout>
    </AnalyticsProvider>
  );
}
